import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, formatIst, inboxApi, type CatalogProduct, type ProductInput } from '@wa/shared';

/**
 * The catalogue, edited from here.
 *
 * Meta's Commerce Manager is still the full tool — variants, bulk upload, sets,
 * review appeals — and this does not pretend otherwise. It is the small business's
 * version: a name, a description, a price, a picture, in stock or not. The list
 * is the mirror the composer's picker reads; a write goes to Meta first and the
 * row is refreshed from what Meta then holds, so this screen never shows a
 * product WhatsApp does not have.
 *
 * Review is the part people do not expect. A product WhatsApp accepted is not
 * yet on phones — it is reviewed, usually within the hour, and a rejection comes
 * with reasons. Both are shown on the row, because "I added it and nobody can
 * see it" is otherwise a support call.
 */
export function CataloguePanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const catalogue = useQuery({ queryKey: ['catalogue'], queryFn: () => inboxApi.catalogProducts(), staleTime: 60_000 });
  const setData = (data: { items: CatalogProduct[]; state: unknown }) => queryClient.setQueryData(['catalogue'], data);
  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);

  const refresh = useMutation({
    mutationFn: inboxApi.syncCatalogue,
    onSuccess: (data) => { setError(null); setData(data); },
    onError: (e) => fail(e, 'Could not refresh the catalogue.'),
  });

  const create = useMutation({
    mutationFn: (input: ProductInput) => inboxApi.createProduct(input),
    onSuccess: () => { setError(null); setAdding(false); void refresh.mutateAsync(); },
    onError: (e) => fail(e, 'WhatsApp did not accept the product.'),
  });

  const update = useMutation({
    mutationFn: ({ retailerId, input }: { retailerId: string; input: ProductInput }) => inboxApi.updateProduct(retailerId, input),
    onSuccess: () => { setError(null); setEditing(null); void refresh.mutateAsync(); },
    onError: (e) => fail(e, 'WhatsApp did not accept the change.'),
  });

  const remove = useMutation({
    mutationFn: (retailerId: string) => inboxApi.deleteProduct(retailerId),
    onSuccess: () => { setError(null); setConfirming(null); void refresh.mutateAsync(); },
    onError: (e) => fail(e, 'Could not remove the product.'),
  });

  const items = catalogue.data?.items ?? [];
  const state = catalogue.data?.state;
  // "No catalogue" only when WhatsApp answered and said so; a refused read is
  // the banner above, not this — the two need different people to fix them.
  const noCatalogue = !!state && !state.catalogId && !state.syncProblem;
  const busy = create.isPending || update.isPending || remove.isPending;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">{error}</p>
      )}

      {state?.syncProblem && (
        <p className="rounded border border-(--color-warn) px-3 py-2 text-small">
          <b>The catalogue could not be read from WhatsApp.</b> {state.syncProblem}
          {items.length > 0 && state.syncedAt && <span className="text-(--color-muted)"> Showing the copy from {formatIst(state.syncedAt)}.</span>}
        </p>
      )}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <header className="flex flex-wrap items-center gap-2 border-b border-(--color-line) px-3 py-2.5 sm:px-4">
          <h2 className="min-w-0 flex-1 text-body font-semibold">
            {state?.name ?? 'Catalogue'}
            {state?.syncedAt && <span className="ml-2 text-micro font-normal text-(--color-muted)">refreshed {formatIst(state.syncedAt)}</span>}
          </h2>
          <button
            type="button" onClick={() => refresh.mutate()} disabled={refresh.isPending}
            className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small text-(--color-ink-soft) hover:bg-(--color-sunken) disabled:opacity-50"
          >
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button" onClick={() => { setError(null); setEditing(null); setAdding(true); }} disabled={adding || noCatalogue}
            className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-1.5 text-small font-semibold text-(--color-brand-ink) disabled:opacity-40"
          >
            Add product
          </button>
        </header>

        {adding && (
          <div className="border-b border-(--color-line) px-3 py-3 sm:px-4">
            <ProductForm
              busy={create.isPending}
              onCancel={() => setAdding(false)}
              onSubmit={(input) => create.mutate(input)}
              onError={setError}
            />
          </div>
        )}

        {catalogue.isPending && <p className="px-3 py-3 text-small text-(--color-muted) sm:px-4">Loading the catalogue…</p>}
        {noCatalogue && (
          <p className="px-3 py-3 text-small text-(--color-muted) sm:px-4">
            No catalogue is connected to your WhatsApp number yet. It is created and connected in Meta&rsquo;s WhatsApp Manager &rarr; Catalogue; products can be added here once it is.
          </p>
        )}
        {!catalogue.isPending && !noCatalogue && items.length === 0 && !adding && (
          <p className="px-3 py-3 text-small text-(--color-muted) sm:px-4">
            No products yet. Add one, and it can be sent from any chat once WhatsApp has reviewed it.
          </p>
        )}

        <ul className="divide-y divide-(--color-line)">
          {items.map((p) => (
            <li key={p.retailerId} className="px-3 py-2.5 sm:px-4">
              {editing === p.retailerId ? (
                <ProductForm
                  product={p}
                  busy={update.isPending}
                  onCancel={() => setEditing(null)}
                  onSubmit={(input) => update.mutate({ retailerId: p.retailerId, input })}
                  onError={setError}
                />
              ) : (
                <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5">
                  <Thumb product={p} />
                  <div className="min-w-0 flex-1 basis-56">
                    <p className="text-small font-medium">
                      {p.name}
                      {p.availability && p.availability !== 'in stock' && (
                        <span className="ml-2 rounded-full border border-(--color-line) px-1.5 py-0.5 text-micro font-normal text-(--color-muted)">{p.availability}</span>
                      )}
                    </p>
                    <p className="text-small text-(--color-ink-soft)">{p.price ?? '—'}</p>
                    {p.description && <p className="mt-0.5 line-clamp-2 text-micro text-(--color-muted)">{p.description}</p>}
                    <p className="mt-0.5 text-micro text-(--color-muted)">
                      <ReviewChip product={p} />
                      <span className="ml-2 font-mono">{p.retailerId}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {confirming === p.retailerId ? (
                      <>
                        <button
                          type="button" disabled={busy} onClick={() => remove.mutate(p.retailerId)}
                          className="tap rounded border border-(--color-bad) px-2 py-1 text-small text-(--color-bad) disabled:opacity-50"
                        >
                          {remove.isPending ? 'Removing…' : 'Remove from WhatsApp'}
                        </button>
                        <button type="button" onClick={() => setConfirming(null)} className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted)">Keep</button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button" disabled={busy}
                          onClick={() => { setError(null); setAdding(false); setEditing(p.retailerId); }}
                          className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink) disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button" disabled={busy} onClick={() => setConfirming(p.retailerId)}
                          className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-bad) disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      <p className="text-micro text-(--color-muted)">
        Variants, bulk upload and review appeals are done in Meta&rsquo;s Commerce Manager; changes made there appear here on the next refresh.
      </p>
    </div>
  );
}

/**
 * The review verdict, in words. "Approved" is the only state a customer can see;
 * the others say what to do.
 */
function ReviewChip({ product }: { product: CatalogProduct }) {
  const status = (product.reviewStatus ?? '').toLowerCase();
  if (product.imageStatus === 'FETCH_FAILED') {
    return <span className="text-(--color-bad)">WhatsApp could not fetch the picture — upload it again</span>;
  }
  if (status === 'approved') return <span className="text-(--color-good)">Approved · visible to customers</span>;
  if (status === 'rejected') {
    return (
      <span className="text-(--color-bad)">
        Rejected by WhatsApp{product.reviewReasons ? `: ${product.reviewReasons.toLowerCase().replace(/_/g, ' ')}` : ''}
      </span>
    );
  }
  if (status === 'pending') return <span className="text-(--color-warn)">Under review — not on phones yet</span>;
  if (status === 'outdated') return <span className="text-(--color-warn)">Changed since review — being re-checked</span>;
  // No verdict from WhatsApp yet (a product added moments ago, or one Meta has
  // not looked at). Not "under review": that was shown on products customers
  // could already see, and the honest thing is to say nothing was reported.
  if (status === '' || status === 'no_review') return <span className="text-(--color-muted)">Review status not reported yet — Refresh in a while</span>;
  return <span className="text-(--color-muted)">{status}</span>;
}

function Thumb({ product }: { product: CatalogProduct }) {
  return product.imageUrl
    ? <img src={product.imageUrl} alt="" className="size-14 shrink-0 rounded object-cover" loading="lazy" />
    : <span className="grid size-14 shrink-0 place-items-center rounded bg-(--color-sunken) text-(--color-muted)" aria-hidden="true">🛍️</span>;
}

// ------------------------------------------------------------- the form ----

interface FormProps {
  product?: CatalogProduct;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: ProductInput) => void;
  onError: (message: string | null) => void;
}

/** "25,500.00 INR" → "25500.00", for the price box when editing. */
function priceOf(product: CatalogProduct | undefined): string {
  if (!product?.price) return '';
  const digits = product.price.replace(/[^0-9.]/g, '');
  return digits;
}

function ProductForm({ product, busy, onCancel, onSubmit, onError }: FormProps) {
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [price, setPrice] = useState(priceOf(product));
  const [currency, setCurrency] = useState(product?.currency ?? 'INR');
  const [link, setLink] = useState(product?.link ?? '');
  const [inStock, setInStock] = useState(product?.availability !== 'out of stock');
  const [imageUrl, setImageUrl] = useState<string | null>(product?.imageUrl ?? null);
  const [extra, setExtra] = useState<string[]>(product?.extraImageUrls ?? []);
  const [origin, setOrigin] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const extraInput = useRef<HTMLInputElement>(null);
  const nameBox = useRef<HTMLInputElement>(null);

  useEffect(() => { nameBox.current?.focus(); }, []);

  const upload = useMutation({
    mutationFn: (file: File) => inboxApi.uploadProductImage(file),
    onSuccess: (r) => { onError(null); setImageUrl(r.imageUrl); },
    onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not upload the picture.'),
  });

  // More pictures, one upload at a time so a failure names the file. WhatsApp
  // shows up to ten on a product: the main one plus nine.
  const MAX_EXTRA = 9;
  const uploadExtra = useMutation({
    mutationFn: async (files: File[]) => {
      const urls: string[] = [];
      for (const f of files) urls.push((await inboxApi.uploadProductImage(f)).imageUrl);
      return urls;
    },
    onSuccess: (urls) => { onError(null); setExtra((e) => [...e, ...urls].slice(0, MAX_EXTRA)); },
    onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not upload a picture.'),
  });

  const complete = name.trim() !== '' && price.trim() !== '' && /^[A-Za-z]{3}$/.test(currency.trim()) && !!imageUrl;
  const input = 'w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small outline-none focus:border-(--color-brand)';
  const label = 'mb-1 block text-micro font-medium uppercase tracking-wide text-(--color-faint)';

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complete || busy) return;
        onSubmit({
          name: name.trim(),
          description: description.trim(),
          price: price.trim(),
          currency: currency.trim().toUpperCase(),
          imageUrl,
          link: link.trim(),
          availability: inStock ? 'in stock' : 'out of stock',
          originCountry: origin.trim() || null,
          extraImageUrls: extra,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
        {/* The picture. Uploaded first, so the create carries a URL Meta can fetch. */}
        <div>
          <span className={label}>Picture</span>
          <button
            type="button" onClick={() => fileInput.current?.click()} disabled={upload.isPending}
            className="grid size-32 place-items-center overflow-hidden rounded-(--radius-field) border border-dashed border-(--color-line) bg-(--color-paper) text-micro text-(--color-muted) hover:border-(--color-brand)"
            title="JPG, PNG or WebP, up to 8 MB. Square, at least 500 × 500."
          >
            {upload.isPending ? 'Uploading…' : imageUrl ? <img src={imageUrl} alt="" className="size-full object-cover" /> : 'Add a picture'}
          </button>
          <input
            ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }}
          />
          <span className="mt-1 block text-micro text-(--color-muted)">Square, plain background. Text-heavy pictures fail review.</span>

          {/* The rest of the pictures: small, removable, in the order WhatsApp shows them. */}
          <span className={`${label} mt-3`}>More pictures</span>
          <div className="flex flex-wrap gap-1.5">
            {extra.map((url, i) => (
              <span key={url} className="relative">
                <img src={url} alt="" className="size-10 rounded object-cover" />
                <button
                  type="button" onClick={() => setExtra((e) => e.filter((_, j) => j !== i))} aria-label="Remove picture"
                  className="absolute -top-1.5 -right-1.5 grid size-4 place-items-center rounded-full bg-(--color-ink) text-[10px] leading-none text-(--color-paper)"
                >×</button>
              </span>
            ))}
            {extra.length < MAX_EXTRA && (
              <button
                type="button" onClick={() => extraInput.current?.click()} disabled={uploadExtra.isPending}
                className="grid size-10 place-items-center rounded border border-dashed border-(--color-line) text-micro text-(--color-muted) hover:border-(--color-brand) disabled:opacity-50"
                title="Up to nine more"
              >
                {uploadExtra.isPending ? '…' : '+'}
              </button>
            )}
          </div>
          <input
            ref={extraInput} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => { const fs = Array.from(e.target.files ?? []).slice(0, MAX_EXTRA - extra.length); if (fs.length) uploadExtra.mutate(fs); e.target.value = ''; }}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className={label}>Name</span>
            <input ref={nameBox} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} className={input} placeholder="RadeNext HRMS — annual licence" />
          </label>
          <label className="block">
            <span className={label}>Price</span>
            <span className="flex items-stretch rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) focus-within:border-(--color-brand)">
              <input
                value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))} aria-label="Currency"
                className="w-14 border-r border-(--color-line) bg-transparent px-2 py-2 text-center font-mono text-small uppercase outline-none"
              />
              <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="25500.00" aria-label="Price" className="min-w-0 flex-1 bg-transparent px-2.5 py-2 text-small outline-none" />
            </span>
          </label>
          <label className="block">
            <span className={label}>Availability</span>
            <select value={inStock ? 'in' : 'out'} onChange={(e) => setInStock(e.target.value === 'in')} className={input}>
              <option value="in">In stock</option>
              <option value="out">Out of stock</option>
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className={label}>Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={5000} className={`${input} resize-y`} placeholder="What it is, what is included, how long it takes." />
          </label>
          <label className="block">
            <span className={label}>Web page (optional)</span>
            <input value={link} onChange={(e) => setLink(e.target.value)} className={input} placeholder="https://" />
          </label>
          <label className="block">
            <span className={label}>Country of origin (optional)</span>
            <input value={origin} onChange={(e) => setOrigin(e.target.value.toUpperCase().slice(0, 2))} className={input} placeholder="IN" />
            <span className="mt-1 block text-micro text-(--color-muted)">India&rsquo;s e-commerce rules ask for it on every listing.</span>
          </label>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit" disabled={!complete || busy || upload.isPending || uploadExtra.isPending}
          className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-small font-semibold text-(--color-brand-ink) disabled:opacity-40"
        >
          {busy ? 'Sending to WhatsApp…' : product ? 'Save' : 'Add to catalogue'}
        </button>
        <button type="button" onClick={onCancel} className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small">Cancel</button>
        {!product && <span className="self-center text-micro text-(--color-muted)">WhatsApp reviews new products before customers see them, usually within the hour.</span>}
      </div>
    </form>
  );
}
